// Module 11 — pure chart projections from Snapshot.totals (no DOM, no uPlot). Each ChartDef maps a
// snapshot's totals to the y-values of its series. GenerationStack is keyed by GRID ROLE
// (slack/import/gas) not per-generator fuel, because generator nameplate metadata is not in state.v1
// and this module imports only domain + state (see dashboards.v1 freeze note).
import type { Snapshot } from "../domain/index";

type Totals = Snapshot["totals"];

export interface ChartDef {
  key: string;
  title: string;
  subtitle?: string; // optional nominal-value hint (e.g. "60.00 Hz nominal") so a steady SD line reads
  //                    HEALTHY, not dead/disconnected, when it sits flat at the N-0 secure baseline.
  seriesLabels: string[];
  unit?: string; // y-axis label (default "MW")
  // a chart projects EITHER from totals (most) OR from the whole snapshot (price / flows / loads charts).
  // projectSnap takes precedence in the renderer. At least one is present.
  project?(totals: Totals): number[];
  projectSnap?(snap: Snapshot): number[];
}

export const CONSUMPTION: ChartDef = {
  key: "consumption",
  title: "Consumption (MW)",
  seriesLabels: ["gross demand (incl. solar)", "net load (demand − solar)"],
  project: (t) => [t.grossDemandMw, t.netLoadMw],
};

export const GENERATION_STACK: ChartDef = {
  key: "generation",
  title: "Generation (by role)",
  // dispatch-realism v3: nuclear is now the FLAT baseload band (t.nuclearMw, ALL nuclear incl.
  // import Bruce/Pickering — pinned, does not chase demand); in-GTA gas is the LOAD-FOLLOWER that
  // VARIES with demand; import = flexible imports; "other" closes the gap (hydro, WTE, CHP, storage,
  // behind-meter solar) = genMw − nuclear − gas − import. The four bands sum to genMw. Falls back to
  // slackMw (Darlington only) for pre-v3.2 snapshots that lack nuclearMw.
  seriesLabels: ["nuclear (baseload)", "in-GTA gas", "import", "other (hydro+storage+solar)"],
  project: (t) => {
    const nuc = t.nuclearMw ?? t.slackMw;
    // P16 C68: read the single-source otherGenMw (computed once in assembly); fallback for pre-v8 snapshots.
    return [nuc, t.gtaGasMw, t.importMw, t.otherGenMw ?? Math.max(0, t.genMw - nuc - t.gtaGasMw - t.importMw)];
  },
};

export const BALANCE: ChartDef = {
  key: "balance",
  title: "Balance (MW)",
  subtitle: "0 curtailed / 0 unserved = fully served",
  seriesLabels: ["supply margin (±MW)", "curtailed load", "unserved load"],
  project: (t) => [t.supplyMarginMw ?? (t.genMw - t.netLoadMw), t.curtailedMw, t.unservedMw],
};

// ── expanded engine-connected charts (all bind to existing snapshot data, no engine change) ─────────

// dispatch role bands (v4 totals): the merit stack by operating role over time.
export const ROLE_BANDS: ChartDef = {
  key: "roles",
  title: "Dispatch role bands (MW)",
  seriesLabels: ["baseload", "load-following", "peaker", "VRE (wind+solar)", "storage net"],
  project: (t) => [t.baseloadMw ?? 0, t.loadFollowingMw ?? 0, t.peakerMw ?? 0, t.vreMw ?? 0, t.storageNetMw ?? 0],
};

// system marginal price λ — the cost of the next MWh ($/MWh), what drives plant switching.
export const MARGINAL_COST: ChartDef = {
  key: "lambda",
  title: "System marginal price λ ($/MWh)",
  subtitle: "merit clearing price + any scarcity adder (VOLL) when the grid runs short",
  unit: "$/MWh",
  // Wave 5 reconnect: split the VOLL scarcity adder out of λ so the stacked height = the FULL clearing price and
  // you can see WHEN the price is a scarcity signal (the adder spikes toward VOLL $2000 during a real shortage)
  // versus a normal merit price. Both fields are already on the wire.
  seriesLabels: ["merit λ", "scarcity → VOLL"],
  project: (t) => {
    const scarcity = t.scarcityAdderPerMwh ?? 0;
    return [Math.max(0, (t.marginalCostPerMwh ?? 0) - scarcity), scarcity];
  },
};

// retail bill decomposition ($/kWh) — commodity (RPP) + the delivery legs + λ converted to $/kWh.
export const PRICES: ChartDef = {
  key: "prices",
  title: "Retail price by component ($/kWh)",
  unit: "$/kWh",
  seriesLabels: ["commodity (RPP)", "transmission", "distribution", "λ (→$/kWh)"],
  projectSnap: (s) => [
    s.price.retailPerKwh,
    s.price.transmissionPerKwh ?? 0,
    s.price.distributionPerKwh ?? 0,
    (s.totals.marginalCostPerMwh ?? 0) / 1000,
  ],
};

// CO₂ emissions per tick (kg) — climbs as gas/peakers come on, falls on a clean baseload+VRE hour.
export const EMISSIONS: ChartDef = {
  key: "emissions",
  title: "CO₂ emissions (kg/tick)",
  unit: "kg",
  seriesLabels: ["CO₂"],
  project: (t) => [t.emissionsKg ?? 0],
};

// T&D loss breakdown (MW) — was transmission-only (lossMw); P11 (C31) added distLossMw (last-mile
// distribution I2R loss, modeled per-load) + totalTndLossMw (their sum), never charted until now — the
// old single-series chart silently omitted roughly half the real loss picture.
export const LOSSES: ChartDef = {
  key: "losses",
  title: "Transmission & distribution losses (MW)",
  // F6.2 added the BESS √η round-trip conversion loss (charge/discharge/FFR) — applied to SoC but previously discarded.
  // Wave 5: + transformer IRON (no-load) loss (t.ironLossMw), the constant magnetising loss on every energized winding.
  seriesLabels: ["transmission (DC marginal)", "distribution (last-mile)", "total T&D", "BESS conversion (√η)", "transformer iron (no-load)"],
  project: (t) => [t.lossMw ?? 0, t.distLossMw ?? 0, t.totalTndLossMw ?? ((t.lossMw ?? 0) + (t.distLossMw ?? 0)), t.bessConversionLossMw ?? 0, t.ironLossMw ?? 0],
};

// intertie exchange — import in, export out (MW) across the transmission ties.
export const INTERTIE: ChartDef = {
  key: "intertie",
  title: "Intertie exchange (MW)",
  seriesLabels: ["import", "export"],
  project: (t) => [t.importMw, t.exportMw ?? 0],
};

// load service status counts over time (normal / stressed / shed / dark).
export const LOAD_STATUS: ChartDef = {
  key: "loadstatus",
  title: "Load service status (count)",
  unit: "loads",
  seriesLabels: ["normal", "stressed", "shed", "dark"],
  projectSnap: (s) => {
    const c = { normal: 0, stressed: 0, shed: 0, dark: 0 };
    for (const l of s.loads ?? []) c[l.status]++;
    return [c.normal, c.stressed, c.shed, c.dark];
  },
};

// settlement cash-flow by leg ($/tick) — commodity → generators, transmission → Hydro One, dist → LDC.
// The 'p2p' leg was DROPPED 2026-06-30 (P14b cleanup): the 3-leg retail settlement chain never emits a
// finance flow with kind==='p2p' (market.ts P2P is NON-NORMATIVE — see economics rules), so that series sat
// permanently flat-zero and read as a dead/broken chart. Any p2p flow, were one ever emitted, is ignored here.
export const SETTLEMENT: ChartDef = {
  key: "settlement",
  title: "Settlement by leg ($/tick)",
  unit: "$",
  seriesLabels: ["commodity", "transmission", "distribution"],
  projectSnap: (s) => {
    const c = { commodity: 0, transmission: 0, distribution: 0 };
    for (const f of s.flows) {
      if (f.layer !== "finance") continue;
      const k = f.kind ?? "commodity";
      if (k in c) c[k as keyof typeof c] += f.magnitude;
    }
    return [c.commodity, c.transmission, c.distribution];
  },
};

// ── engine.v2 System-Dynamics telemetry (Phase 1 / U3) ──────────────────────────────────────────────
// The COI frequency, inertia, reserves, BESS, demand-response, comms-health and settlement totals the engine
// computes EVERY tick (ts-engine.ts:700-719) but that NO chart consumed — the "advanced engine" made visible.
// All `?? <neutral>`-guarded so a pre-v2 snapshot renders a flat baseline, never throws. Bind to totals only.
export const FREQUENCY: ChartDef = {
  key: "frequency", title: "System frequency (Hz)", subtitle: "60.00 Hz nominal — flat = healthy", unit: "Hz",
  seriesLabels: ["frequency", "nadir (post-disturbance min)"],
  // P16 C84: plot nadir only on a disturbance tick; hold 60 on steady ticks (nadir is a transient peak, not the
  // hour's steady value — NERC BAL-003 Point C vs Point B). Pre-v8 fallback: always plot (no isDisturbance).
  project: (t) => [t.frequencyHz ?? 60, (t.isDisturbance ?? true) ? (t.freqNadirHz ?? 60) : (t.frequencyHz ?? 60)],
};
export const ROCOF: ChartDef = {
  key: "rocof", title: "Rate of change of frequency (Hz/s)", subtitle: "0 Hz/s = steady", unit: "Hz/s",
  seriesLabels: ["RoCoF"],
  // P16 C84: plot RoCoF only on a disturbance tick; hold 0 on steady ticks.
  project: (t) => [(t.isDisturbance ?? true) ? (t.rocofHzPerS ?? 0) : 0],
};
export const INERTIA: ChartDef = {
  key: "inertia", title: "System inertia constant H (s)", subtitle: "typ. ~4s for the Ontario fleet — falls as VRE displaces synchronous units", unit: "s",
  seriesLabels: ["inertia H (falls as VRE rises)"],
  project: (t) => [t.inertiaConstantS ?? 0],
};
export const ACE: ChartDef = {
  key: "ace", title: "Area control error (MW)", subtitle: "0 MW = balanced",
  seriesLabels: ["ACE"],
  project: (t) => [t.aceMw ?? 0],
};
export const PRIMARY_RESERVE: ChartDef = {
  key: "primaryreserve", title: "Primary reserve + BESS FFR (MW)", subtitle: "0 MW = no active frequency event",
  seriesLabels: ["primary reserve", "BESS fast-frequency response"],
  project: (t) => [t.primaryReserveMw ?? 0, t.bessFfrMw ?? 0],
};
export const BESS_SOC: ChartDef = {
  key: "bessoc", title: "Grid BESS state of charge (MWh)", unit: "MWh",
  seriesLabels: ["BESS SoC"],
  project: (t) => [t.bessSocMwh ?? 0],
};
// P15 (post-audit fix): the wholesale-lambda DR loop this chart originally plotted was RETIRED and
// re-targeted to a TOU retail-tariff load-shift mechanism (Load.shiftableFrac -> totals.deferredMwh) —
// this chart kept charting the OLD (now largely inert) mechanism while the NEW one sat completely
// unplotted anywhere. Widened to a 2nd series so the chart reflects what's actually live post-P15.
export const COMMS_HEALTH: ChartDef = {
  key: "commshealth", title: "Comms-layer health (fraction)", subtitle: "1.0 = fully healthy", unit: "frac",
  seriesLabels: ["comms health (1 = fully healthy)"],
  project: (t) => [t.commsHealthFrac ?? 1],
};

// ── P-CHARTS (2026-07-06 pt.2, UI/UX overhaul): P0-priority gaps found by an exhaustive audit —
// engine data the P7/P9/P10/P5-P6 phases compute EVERY TICK but that NO panel ever surfaced. Each below
// binds to totals fields that already exist (verified against domain/index.ts directly; none invented).

// bus voltage profile (pu) — the #1 thing a grid operator watches alongside frequency; P7 (LinDistFlow)
// computes a per-bus vMagPu every tick and ships 3 summary totals, but nothing charted them until now.
// ANSI C84.1 Range A sag is <=0.95 pu; sagBusCount rising from 0 is the leading early-warning indicator.
export const VOLTAGE: ChartDef = {
  key: "voltage", title: "Bus voltage profile (pu)", subtitle: "1.0 pu nominal — ANSI C84.1 Range A sag <0.95",
  unit: "pu",
  seriesLabels: ["min bus voltage", "max bus voltage"],
  project: (t) => [t.minVMagPu ?? 1, t.maxVMagPu ?? 1],
};
export const VOLTAGE_SAG_COUNT: ChartDef = {
  key: "voltagesag", title: "Buses in voltage sag (count)", subtitle: "0 = every bus within ANSI Range A",
  unit: "buses",
  seriesLabels: ["sagging buses"],
  project: (t) => [t.sagBusCount ?? 0],
};

// unit-commitment status — P9 built a full min-up/min-down/ramp-lockout subsystem; a persistent
// decommitLatchedUnits > 0 is documented (domain.v7 P9 note) as signalling an over-committed fleet
// burning no-load cost it can't recover — exactly the kind of thing a control room watches for.
export const UNIT_COMMITMENT: ChartDef = {
  key: "unitcommitment", title: "Unit commitment status (count)", subtitle: "decommit-latched > 0 = over-committed fleet",
  unit: "units",
  seriesLabels: ["committed units", "ramp-bound units", "decommit-latched units"],
  project: (t) => [t.committedUnits ?? 0, t.rampBoundUnits ?? 0, t.decommitLatchedUnits ?? 0],
};

// hydro-reservoir state & water value — the direct analogue of BESS_SOC (which already had a chart);
// P10 built a full water-value ReservoirScheduler with 4 always-emitted totals, none ever charted.
export const HYDRO_RESERVOIR: ChartDef = {
  key: "hydroreservoir", title: "Hydro reservoir state (MWh) & water value ($/MWh)",
  seriesLabels: ["reservoir SoC (MWh)", "fleet water value ($/MWh)", "discharge (MW)"],
  project: (t) => [t.hydroReservoirMwh ?? 0, t.hydroWaterValuePerMwh ?? 0, t.hydroReservoirDischargeMw ?? 0],
};

// N-1 security margin — the headline number in every real control room (MSSC drives the operating-
// reserve requirement). Pairs the existing reserveMw against the largest single contingency so an
// operator can see the ADEQUACY MARGIN (reserve − worst contingency) at a glance, not two disconnected
// numbers on separate panels. P12 protection-coordination counts ride alongside (0 = healthy at N-0).
export const SECURITY_MARGIN: ChartDef = {
  key: "securitymargin", title: "N-1 security margin (MW)", subtitle: "reserve should exceed the largest single contingency",
  seriesLabels: ["spinning reserve", "largest single contingency"],
  project: (t) => [t.reserveMw, t.largestContingencyMw ?? 0],
};
export const PROTECTION_STATUS: ChartDef = {
  key: "protectionstatus", title: "Protection coordination (count)", subtitle: "0/0 = healthy, no contingency active",
  unit: "count",
  seriesLabels: ["locked-out breakers", "un-tripped (failed) protection"],
  project: (t) => [t.lockedOutCount ?? 0, t.protectionUntripCount ?? 0],
};

// ── Wave 5: 9 NEW charts wiring engine variables that were computed every tick but never shown. Every field
// below already rides the wire (Snapshot / totals) → dashboards-only, golden-neutral, no protocol change. ──

// Economics: how far apart the per-bus prices are — a live congestion / locational-value signal.
export const NODAL_SPREAD: ChartDef = {
  key: "nodalspread", title: "Nodal price spread ($/MWh)",
  subtitle: "min/max LMP across buses — widens as the grid congests (0 spread = uncongested)",
  unit: "$/MWh",
  seriesLabels: ["min LMP", "max LMP", "spread (max−min)"],
  projectSnap: (s) => {
    const p = s.nodalPrices;
    if (!p || p.length === 0) return [0, 0, 0];
    let lo = Infinity, hi = -Infinity;
    for (const n of p) { if (n.lmpPerMwh < lo) lo = n.lmpPerMwh; if (n.lmpPerMwh > hi) hi = n.lmpPerMwh; }
    return [lo, hi, hi - lo];
  },
};

// Security: how many limits are binding the dispatch right now.
export const CONGESTION: ChartDef = {
  key: "congestion", title: "Network congestion (binding constraints)",
  subtitle: "0 = no thermal / import-ATC limit is constraining the dispatch",
  unit: "count",
  seriesLabels: ["binding constraints"],
  project: (t) => [t.bindingBranchCount ?? 0],
};

// Security: N-1 adequacy — how many lines would exceed their limit after the worst single contingency.
export const N1_INSECURE: ChartDef = {
  key: "n1insecure", title: "N-1 insecure branches (count)",
  subtitle: "0 = every single-contingency leaves all lines within limits (N-1 secure)",
  unit: "count",
  seriesLabels: ["branches >100% post-contingency"],
  projectSnap: (s) => [s.branchFlows.reduce((n, b) => n + ((b.contingencyLoadingPct ?? 0) > 100 ? 1 : 0), 0)],
};

// Security: the single most-loaded line, as a % of its rating.
export const WORST_LOADING: ChartDef = {
  key: "worstloading", title: "Worst branch loading (%)",
  subtitle: "the most-loaded line; 100% = at its rating, >130% would trip",
  unit: "%",
  seriesLabels: ["worst branch loading"],
  project: (t) => [t.invariant?.worstBranchLoadingPct ?? 0],
};

// Environment→infrastructure: THE answer to "temperature does nothing". Line rating falls below 1 and conductor
// resistance rises above 1 as it heats; both bite the grid. Drag the temperature slider and watch these move.
export const HEAT_DERATES: ChartDef = {
  key: "heatderates", title: "Heat derates (× nominal)",
  subtitle: "temperature bites the grid: line rating ↓ below 1, conductor resistance ↑ above 1",
  unit: "×",
  seriesLabels: ["line rating factor (≤1)", "conductor resistance factor (≥1)"],
  project: (t) => [t.lineRatingFactor ?? 1, t.resistanceFactor ?? 1],
};

// Voltage: the distribution of per-bus voltage — a left tail means buses are sagging.
export const VOLTAGE_HISTOGRAM: ChartDef = {
  key: "voltagehist", title: "Voltage distribution (buses by pu band)",
  subtitle: "most buses near 1.0 pu = healthy; a growing <0.95 tail = voltage sag under load",
  unit: "buses",
  seriesLabels: ["<0.95", "0.95–0.98", "0.98–1.02", "1.02–1.05", ">1.05"],
  projectSnap: (s) => {
    const bins: [number, number, number, number, number] = [0, 0, 0, 0, 0];
    for (const v of s.busVMagPu.values()) {
      if (v <= 0.5) continue; // de-energized bus — not a voltage-profile point
      if (v < 0.95) bins[0]++; else if (v < 0.98) bins[1]++; else if (v <= 1.02) bins[2]++; else if (v <= 1.05) bins[3]++; else bins[4]++;
    }
    return bins;
  },
};

// Frequency & stability: automatic under-frequency load shedding fired by the Hz ladder.
export const UFLS: ChartDef = {
  key: "ufls", title: "Under-frequency load shed (MW)",
  subtitle: "0 = frequency above the UFLS ladder (no automatic shed); spikes on a severe under-frequency event",
  unit: "MW",
  seriesLabels: ["UFLS shed"],
  project: (t) => [t.uflsShedMw ?? 0],
};

// Supply: the cost of keeping the committed fleet ready — start-up + no-load ($/tick).
export const UC_COST: ChartDef = {
  key: "uccost", title: "Unit-commitment cost ($/tick)",
  subtitle: "start-up + no-load cost of the committed fleet (rises when more units must be held online)",
  unit: "$",
  seriesLabels: ["start-up", "no-load"],
  project: (t) => [t.startupCostDollars ?? 0, t.noLoadCostDollars ?? 0],
};

// Expert diagnostics: the engine's own conservation self-check. ~0 in steady state; a transient blip on a
// trip/self-heal tick is normal (the ΔP that drives the frequency deviation), not a leak.
export const PHYSICS_RESIDUAL: ChartDef = {
  key: "physicsresidual", title: "Physics residual (MW) — diagnostics",
  subtitle: "conservation sits at ~0; a brief blip on a trip tick is the real ΔP, not an error",
  unit: "MW",
  seriesLabels: ["slack absorbed", "conservation residual", "worst KCL residual"],
  project: (t) => [t.slackAbsorbedMw ?? 0, t.conservationResidualMw ?? 0, t.invariant?.worstResidualMw ?? 0],
};

export const CHART_DEFS: ChartDef[] = [
  // UI overhaul Wave 4a — REMOVED 3 charts: RESERVE (single-series reserveMw, strictly dominated by SECURITY_MARGIN
  // which pairs reserve against the largest contingency), SETTLEMENT_TOTAL (exactly Σ of the by-leg SETTLEMENT
  // chart), and DEMAND_RESPONSE (structurally pinned 0 — shiftableFrac on 0/449 loads, so both series flat-0 forever).
  CONSUMPTION, GENERATION_STACK, BALANCE,
  ROLE_BANDS, MARGINAL_COST, PRICES, EMISSIONS, LOSSES, INTERTIE, LOAD_STATUS, SETTLEMENT,
  // engine.v2 SD telemetry (U3) — previously dead totals, now charted
  FREQUENCY, ROCOF, INERTIA, ACE, PRIMARY_RESERVE, BESS_SOC, COMMS_HEALTH,
  // P-CHARTS (2026-07-06 pt.2) — P0-priority engine data with zero prior visibility
  VOLTAGE, VOLTAGE_SAG_COUNT, UNIT_COMMITMENT, HYDRO_RESERVOIR, SECURITY_MARGIN, PROTECTION_STATUS,
  // UI overhaul Wave 5 — 9 more engine variables that were computed every tick but never charted
  NODAL_SPREAD, CONGESTION, N1_INSECURE, WORST_LOADING, HEAT_DERATES, VOLTAGE_HISTOGRAM, UFLS, UC_COST, PHYSICS_RESIDUAL,
];
